import { Section, VariantBlock } from "../lib/Section";

/**
 * TypographySpecimen, the type-system screenshot target.
 *
 * Exercises the ONE-family system (WS-15 consolidation; supersedes the
 * three-face Onyx+Hex mix of #122):
 *   - Inter (`font-sans` / `font-display`)  everything — body, UI, display
 *   - system mono (`font-mono`)             eyebrows / ids / metrics,
 *                                           tabular-nums (no shipped face)
 *
 * Every block is labeled with its family + the preset token it renders. The
 * sizes come straight from the named `fontSize` scale (`text-display` …
 * `text-eyebrow`) so this also QAs the scale itself.
 */

/** One row of the size-scale table: rendered sample + its token + spec. */
function ScaleRow({
  token,
  spec,
  family,
  className,
  sample,
}: {
  token: string;
  spec: string;
  family: string;
  className: string;
  sample: string;
}) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-3 pe-6 align-baseline">
        <span className={className}>{sample}</span>
      </td>
      <td className="py-3 pe-6 align-baseline">
        <code className="font-mono text-eyebrow text-accent">{token}</code>
      </td>
      <td className="py-3 pe-6 align-baseline font-mono text-caption text-muted-foreground">
        {family}
      </td>
      <td className="py-3 align-baseline font-mono text-caption text-muted-foreground">{spec}</td>
    </tr>
  );
}

export function TypographySpecimenDemo() {
  return (
    <Section
      id="typography-specimen"
      title="TypographySpecimen"
      description="The one-family type system (WS-15): Inter carries body, UI and display (self-hosted via Fontsource); the mono role is a system stack. Faces are referenced only through the --font-* tokens and the named fontSize scale."
    >
      {/* The three roles, side by side — sans and display are both Inter. */}
      <VariantBlock
        label="The three roles, one face"
        caption="hierarchy from the scale, not from mixing families"
      >
        <div className="grid w-full gap-6 md:grid-cols-3">
          <div className="space-y-2">
            <div className="font-mono text-eyebrow uppercase text-muted-foreground">
              font-sans · --font-sans
            </div>
            <div className="font-sans text-title text-foreground">Inter</div>
            <p className="font-sans text-body text-muted-foreground">
              The working face. UI, body copy, forms, control labels, nav, tables.
            </p>
          </div>
          <div className="space-y-2">
            <div className="font-mono text-eyebrow uppercase text-muted-foreground">
              font-display · --font-display
            </div>
            <div className="font-display text-title text-foreground">Inter (display)</div>
            <p className="font-sans text-body text-muted-foreground">
              The display voice — the same family; the variable opsz axis cuts it for hero / stage
              headlines and long-form headings.
            </p>
          </div>
          <div className="space-y-2">
            <div className="font-mono text-eyebrow uppercase text-muted-foreground">
              font-mono · --font-mono
            </div>
            <div className="font-mono text-title text-foreground">System mono</div>
            <p className="font-sans text-body text-muted-foreground">
              The machine role. ids, metrics, counters, eyebrows, kbd hints. Tabular figures by
              default; no shipped face.
            </p>
          </div>
        </div>
      </VariantBlock>

      {/* The display voice on a dark stage, where it is meant to live. */}
      <VariantBlock label="Display on the dark stage" caption="text-display · font-display">
        <div className="w-full rounded-xl bg-foreground p-8">
          <div className="font-mono text-eyebrow uppercase tracking-[0.12em] text-background/60">
            Reports
          </div>
          <h2 className="mt-3 font-display text-display text-background">
            The editorial energy lives here.
          </h2>
          <p className="mt-4 max-w-xl font-sans text-body-lg text-background/70">
            The display step reserved for the large sizes, where the optical-size axis opens the
            letterforms up. This is Inter, self-hosted, no CDN.
          </p>
        </div>
      </VariantBlock>

      {/* An Inter body paragraph at the body + body-lg steps. */}
      <VariantBlock label="Inter body" caption="text-body-lg / text-body · font-sans">
        <div className="max-w-2xl space-y-4">
          <p className="font-sans text-body-lg text-foreground">
            Inter is a neutral grotesque built for UI legibility at small sizes. It carries the
            dense working surface: paragraphs, form fields, table cells, and every control label
            in the product.
          </p>
          <p className="font-sans text-body text-muted-foreground">
            At the body step it stays calm and readable at length. The hierarchy comes from the
            named scale and the face contrast, not from stretching one typeface across every role.
          </p>
        </div>
      </VariantBlock>

      {/* System-mono eyebrows + a tabular-nums numeric table; ids and counters align. */}
      <VariantBlock label="Mono eyebrows + tabular numerals" caption="font-mono · tabular-nums on by default">
        <div className="w-full max-w-xl space-y-4">
          <div className="flex flex-wrap gap-3">
            <span className="font-mono text-eyebrow uppercase text-accent">SOURCES</span>
            <span className="font-mono text-eyebrow uppercase text-accent">ACTIVE</span>
            <span className="font-mono text-eyebrow uppercase text-accent">exp_01H9</span>
            <span className="font-mono text-eyebrow uppercase text-accent">QUEUE · JATIN</span>
          </div>
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 pe-6 font-mono text-eyebrow uppercase text-muted-foreground">
                  Run id
                </th>
                <th className="py-2 pe-6 font-mono text-eyebrow uppercase text-muted-foreground">
                  Tokens
                </th>
                <th className="py-2 font-mono text-eyebrow uppercase text-muted-foreground">
                  Turns
                </th>
              </tr>
            </thead>
            <tbody className="font-mono text-body text-foreground">
              <tr className="border-b border-border">
                <td className="py-1.5 pe-6">run_01H9</td>
                <td className="py-1.5 pe-6">1,843</td>
                <td className="py-1.5">12 / 24</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-1.5 pe-6">run_4421</td>
                <td className="py-1.5 pe-6">920</td>
                <td className="py-1.5">3 / 24</td>
              </tr>
              <tr>
                <td className="py-1.5 pe-6">run_0007</td>
                <td className="py-1.5 pe-6">11,090</td>
                <td className="py-1.5">9 / 24</td>
              </tr>
            </tbody>
          </table>
          <p className="font-sans text-caption text-muted-foreground">
            The digits sit in fixed-width columns; tabular-nums keeps ids and counters aligned
            without jitter, the moment a font-mono element renders.
          </p>
        </div>
      </VariantBlock>

      {/* The full named size scale, each row labeled with its token + spec. */}
      <VariantBlock label="The full size scale" caption="named fontSize tokens, whole-pixel">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="py-2 pe-6 text-left font-mono text-eyebrow uppercase text-muted-foreground">
                Sample
              </th>
              <th className="py-2 pe-6 text-left font-mono text-eyebrow uppercase text-muted-foreground">
                Token
              </th>
              <th className="py-2 pe-6 text-left font-mono text-eyebrow uppercase text-muted-foreground">
                Family
              </th>
              <th className="py-2 text-left font-mono text-eyebrow uppercase text-muted-foreground">
                Size · LH · tracking · weight
              </th>
            </tr>
          </thead>
          <tbody>
            <ScaleRow
              token="text-display"
              family="font-display (Inter)"
              spec="56px · 1.02 · -0.02em · 700"
              className="font-display text-display text-foreground"
              sample="Display"
            />
            <ScaleRow
              token="text-heading"
              family="font-sans"
              spec="30px · 1.1 · -0.02em · 700"
              className="font-sans text-heading text-foreground"
              sample="Heading"
            />
            <ScaleRow
              token="text-title"
              family="font-sans"
              spec="20px · 1.25 · -0.01em · 600"
              className="font-sans text-title text-foreground"
              sample="Title"
            />
            <ScaleRow
              token="text-body-lg"
              family="font-sans"
              spec="17px · 1.6 · 0 · 400"
              className="font-sans text-body-lg text-foreground"
              sample="Body large"
            />
            <ScaleRow
              token="text-body"
              family="font-sans"
              spec="15px · 1.6 · 0 · 400"
              className="font-sans text-body text-foreground"
              sample="Body"
            />
            <ScaleRow
              token="text-label"
              family="font-sans"
              spec="13px · 1.4 · 0 · 500"
              className="font-sans text-label text-foreground"
              sample="Label"
            />
            <ScaleRow
              token="text-caption"
              family="font-sans"
              spec="12px · 1.4 · 0 · 500"
              className="font-sans text-caption text-foreground"
              sample="Caption"
            />
            <ScaleRow
              token="text-eyebrow"
              family="font-mono (uppercase)"
              spec="11px · 1.3 · 0.12em · 600"
              className="font-mono text-eyebrow uppercase text-foreground"
              sample="Eyebrow"
            />
          </tbody>
        </table>
      </VariantBlock>
    </Section>
  );
}
